/**
 * Calendar Screen State Hook
 *
 * Extracts all state management from calendar.tsx to keep the main component under 500 lines.
 * Manages state for events, loading, permissions, calendars, modals, and caching.
 */

import { useMemo, useRef, useState } from 'react';
import type { CalendarEvent } from '@/types/events';
import type { AgendaSection, CalendarMarkedDates, FlatAgendaItem } from '@/types/calendar-ui';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import type { CalendarPreferences } from '@/types/calendar-preferences';
import type { FamilyMember } from '@/types/family-member';
import type { CalendarMemberMapping } from '@/types/calendar-member-mapping';
import { CalendarEventFormatter } from '@/lib/calendar/CalendarEventFormatter';
import { CalendarScreenManager } from '@/lib/calendar/CalendarScreenManager';
import { LocalDateFormatter } from '@/lib/formatters/LocalDateFormatter';
import { Colors } from '@/constants/theme';
import { useCalendarPreferencesManager } from './use-scoped-managers';

// Event window for loading calendar data
const EVENT_WINDOW_DAYS_BEFORE = 15; // 15 days before (past events)
const EVENT_WINDOW_DAYS_AFTER = 45; // 45 days after (future events)

export type EventWindow = {
  start: Date;
  end: Date;
  key: string;
};

const toDateKey = (date: Date): string => {
  return LocalDateFormatter.toDateKey(date);
};

const parseDateString = (value: string): Date => {
  return LocalDateFormatter.fromDateKey(value);
};

export const buildEventWindow = (centerDate: string): EventWindow => {
  const center = parseDateString(centerDate);
  const start = new Date(center);
  start.setDate(start.getDate() - EVENT_WINDOW_DAYS_BEFORE);
  const end = new Date(center);
  end.setDate(end.getDate() + EVENT_WINDOW_DAYS_AFTER);
  return {
    start,
    end,
    key: `${toDateKey(start)}:${toDateKey(end)}`,
  };
};

/**
 * Build event window for a full 6-week month view.
 * Calculates the first Monday before/on month start and 6 weeks from there.
 * Used for monthly calendar view to load all visible dates including overflow days.
 */
export const buildMonthEventWindow = (year: number, month: number): EventWindow => {
  // First day of the month
  const firstOfMonth = new Date(year, month, 1);

  // Find the Monday of the week containing the 1st (firstDay=1 means Monday start)
  const dayOfWeek = firstOfMonth.getDay(); // 0=Sun, 1=Mon, etc.
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Monday start

  const start = new Date(firstOfMonth);
  start.setDate(start.getDate() - daysToSubtract);

  // End is 6 weeks (42 days) from start
  const end = new Date(start);
  end.setDate(end.getDate() + 41); // 42 days total (0-41)

  return {
    start,
    end,
    key: `month-${year}-${String(month + 1).padStart(2, '0')}`,
  };
};

/**
 * Check if a date string falls within a month's 6-week view window
 */
export const isDateInMonthWindow = (dateString: string, year: number, month: number): boolean => {
  const window = buildMonthEventWindow(year, month);
  const date = parseDateString(dateString);
  return date >= window.start && date <= window.end;
};

/**
 * Extract year and month from a date string (YYYY-MM-DD)
 */
export const getYearMonthFromDateString = (dateString: string): { year: number; month: number } => {
  const [year, month] = dateString.split('-').map(Number);
  return { year, month: month - 1 }; // month is 0-indexed
};

type UseCalendarScreenStateProps = {
  accessToken: string | null;
  colorScheme: 'light' | 'dark' | null;
};

export function useCalendarScreenState({ accessToken, colorScheme }: UseCalendarScreenStateProps) {
  // Computed values
  const todayDateString = useMemo(
    () => CalendarEventFormatter.getTodayDateString(),
    [],
  );
  const initialEventWindow = useMemo(
    () => buildEventWindow(todayDateString),
    [todayDateString],
  );

  // Event data state
  const [sections, setSections] = useState<AgendaSection[]>([]);
  const [flatItems, setFlatItems] = useState<FlatAgendaItem[]>([]);
  const [selectedDate, setSelectedDate] = useState(todayDateString);
  const [activeEventWindow, setActiveEventWindow] = useState<EventWindow>(initialEventWindow);

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSilentLoading, setIsSilentLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Permission state
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);

  // Calendar selection state
  const [deviceCalendars, setDeviceCalendars] = useState<DeviceCalendar[]>([]);
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[]>([]);
  const [showCalendarSelector, setShowCalendarSelector] = useState(false);

  // Preferences state
  const [preferences, setPreferences] = useState<CalendarPreferences>({});

  // Event modal state
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [eventModalMode, setEventModalMode] = useState<'create' | 'edit'>('create');
  const [isSubmittingEvent, setIsSubmittingEvent] = useState(false);
  const [showManagementScreen, setShowManagementScreen] = useState(false);

  // Family members state for day view columns
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [calendarMappings, setCalendarMappings] = useState<CalendarMemberMapping[]>([]);

  // Family label filtering state (for monthly view filter)
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);

  // Refs for caching and tracking
  const eventsCacheRef = useRef<
    Map<string, { sections: AgendaSection[]; flatItems: FlatAgendaItem[]; fetchedAt: number }>
  >(new Map());
  const latestEventRequestRef = useRef(0);
  const selectedDateRef = useRef(todayDateString);
  const activeEventWindowRef = useRef<EventWindow>(initialEventWindow);
  const calendarSelectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoadingDataRef = useRef(false); // Tracks any load (silent or blocking)

  // Managers (memoized)
  const manager = useMemo(
    () => new CalendarScreenManager(accessToken),
    [accessToken],
  );

  // Use scoped preferences manager
  const preferencesManager = useCalendarPreferencesManager();

  // Theme colors (memoized)
  const palette = useMemo(() => Colors[colorScheme ?? 'light'], [colorScheme]);

  // Use "Me" member's color as accent color, fallback to palette.primary
  const meMember = useMemo(() => familyMembers.find(m => m.isMe), [familyMembers]);
  const accentColor = meMember?.color ?? palette.primary;

  const backgroundColor = palette.background;
  const textColor = palette.text;
  const borderColor = palette.border;

  // Marked dates for calendar highlighting
  const markedDates = useMemo<CalendarMarkedDates>(() => {
    const marks: CalendarMarkedDates = {};
    marks[selectedDate] = {
      selected: true,
      selectedColor: accentColor,
    };
    return marks;
  }, [selectedDate, accentColor]);

  // Attendee and calendar maps (empty for now - no family member coloring)
  const attendeeColorMap = useMemo(
    () => new Map<string, { color: string; name: string }>(),
    [],
  );

  const linkedCalendarMap = useMemo(
    () => new Map<string, { color: string; name: string }>(),
    [],
  );

  return {
    // Computed values
    todayDateString,
    initialEventWindow,

    // Event data state
    sections,
    setSections,
    flatItems,
    setFlatItems,
    selectedDate,
    setSelectedDate,
    activeEventWindow,
    setActiveEventWindow,

    // Loading states
    isLoading,
    setIsLoading,
    isRefreshing,
    setIsRefreshing,
    isSilentLoading,
    setIsSilentLoading,
    error,
    setError,

    // Permission state
    hasPermission,
    setHasPermission,
    isRequestingPermission,
    setIsRequestingPermission,

    // Calendar selection state
    deviceCalendars,
    setDeviceCalendars,
    selectedCalendarIds,
    setSelectedCalendarIds,
    showCalendarSelector,
    setShowCalendarSelector,

    // Preferences state
    preferences,
    setPreferences,

    // Event modal state
    showEventModal,
    setShowEventModal,
    selectedEvent,
    setSelectedEvent,
    eventModalMode,
    setEventModalMode,
    isSubmittingEvent,
    setIsSubmittingEvent,
    showManagementScreen,
    setShowManagementScreen,

    // Family members state
    familyMembers,
    setFamilyMembers,
    calendarMappings,
    setCalendarMappings,

    // Family label filtering state
    selectedLabelIds,
    setSelectedLabelIds,

    // Refs
    eventsCacheRef,
    latestEventRequestRef,
    selectedDateRef,
    activeEventWindowRef,
    calendarSelectionTimeoutRef,
    isLoadingDataRef,

    // Managers
    manager,
    preferencesManager,

    // Theme colors
    palette,
    accentColor,
    backgroundColor,
    textColor,
    borderColor,

    // Marked dates
    markedDates,

    // Color maps
    attendeeColorMap,
    linkedCalendarMap,
  };
}
